import type { DraftJson } from '@apps/card/routes/CardCustomizer';
import { useCustomizationContext } from '@apps/card/routes/CardCustomizer';
import type { DeliveryPackageKey } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { useEventCallback } from 'yet-another-react-lightbox';
import { useStationeryProductPricingContext } from '../useStationeryProductPricingContext';
import type { StationeryProduct } from '@shared/utils/print-price/types';
import type { AddAttributeFunction } from '../state/state.mapping';
import { getPossibleOptions } from './utils';

export const useJoyDigitalMappings = () => {
  const [_, updateDigitalCustomizations] = useCustomizationContext(draft => draft.config.digitalCustomizations);
  const { activeProduct } = useStationeryProductPricingContext();

  return useEventCallback((addAttribute: AddAttributeFunction, product: StationeryProduct) => {
    // Register possible attribute 'delivery'
    const deliveryAttributeId = 'delivery';
    const deliveryOptions = getPossibleOptions(product, deliveryAttributeId);
    addAttribute(
      {
        productAttributeId: deliveryAttributeId,
        getOptionId: (draftData: DraftJson) => {
          // Get attribute info so if no option is present, we can select option 0.
          const attributeInfo = activeProduct.attributes.find(attr => attr.attributeId === deliveryAttributeId);

          if (!attributeInfo) {
            // Should never happen. Getter is only called by attribute selector
            // which is only rendered if attribute is present in config and product info.
            throw new Error(`Tried to get non-existent attribute ${deliveryAttributeId}`);
          }

          if (draftData.digitalCustomizations) {
            return draftData.digitalCustomizations?.deliveryPackageKey ?? attributeInfo.attributeOptions[0].attributeOption.id;
          }

          throw new Error('No digitalCustomizations');
        },
        setOptionId: (newState: string) => {
          updateDigitalCustomizations(customizations => {
            if (customizations) {
              customizations.deliveryPackageKey = newState as DeliveryPackageKey;
            }
          });
        },
        allowedOptionIDs: deliveryOptions,
      },
      product,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });
};
